import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { ShowPanel, CollapsePanel, ShowColumn } from '../../state/view.actions';
import { NextChapter, PreviousChapter } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { Observable, Subject } from 'rxjs';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { BookmarkActionsEnum } from 'src/app/bookmarks/components/bookmark-actions/bookmark-actions.component';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { ViewState } from '../../state/view.state';
import { takeUntil, filter } from 'rxjs/operators';
import { StoryState } from 'src/app/story/state/story.state';
import { StoryModel } from 'src/app/story/models/story.model';

import * as _ from 'lodash';
import { AnimationService } from 'src/app/engine/animation.service';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';

@Component({
  selector: 'app-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanelComponent implements OnInit, OnDestroy {
  // Story
  @Select(StoryState.getStory)
  story$: Observable<StoryModel>;

  // Bookmark
  @Select(BookmarksState.getSelectedBookmark)
  bookmark$: Observable<BookmarkModel>;

  @Select(StoryChaptersState.getStoryChapters)
  chapters$: Observable<ChapterModel[]>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  selectedChapterIndex$: Observable<number>;

  @Select(StoryChaptersState.getChaptersLoaded)
  chaptersLoaded$: Observable<any>;

  @Select(StoryChaptersState.getAllChaptersReady)
  allChaptersReady$: Observable<boolean>;

  @Input()
  id: string;

  @Input()
  type: StoryType;

  @Input()
  title: string;

  @Input()
  subtitle: string;

  @Input()
  category = '';

  get bookmarkActions(): BookmarkActionsEnum[] {
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);

    return [
      BookmarkActionsEnum.CopyLink, 
      BookmarkActionsEnum.Embed,
      BookmarkActionsEnum.CopyBookmark,
      // add optional organization actions
      ...organization ? [BookmarkActionsEnum.CopyTeamBookmark] : []
    ];
  }

  collapsed = false;
  isAnimated = false;
  showScrubber = false;
  showPlayPause = false;
  chapterCount = 1;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private animationService: AnimationService
  ) { }

  ngOnInit() {
    this.initListeners();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initListeners() {
    // Handle story loaded
    this.story$
      .pipe(
        filter(story => story != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(story => this.handleStoryLoaded(story));


    // Handle chapter loaded
    this.selectedChapter$.pipe(
        filter((chapter) => chapter != null), 
        takeUntil(this.ngUnsubscribe)
      ).subscribe((chapter) => {
        this.handleChapterReady(chapter);
      });


    this.chapters$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(chapters => this.chapterCount = chapters.length);
    
  }

  handleNextClick() {
    this.store.dispatch(new NextChapter());
  }

  handlePreviousClick() {
    this.store.dispatch(new PreviousChapter());
  }

  handleCloseClick() {
    this.store.dispatch(new ShowColumn(false));
  }

  handleStoryLoaded(story: StoryModel) {
    this.showScrubber = _.get(story, 'ui.scrubber') === true;
    this.showPlayPause = _.get(story, 'ui.playPause') === true;
  }

  handleChapterReady(chapter) {
    this.isAnimated = this.animationService.isChapterAnimated(chapter);
  }

  showPanelFooter() {
    if (this.isAnimated && (this.showPlayPause || this.showScrubber)) {
      return true;
    }

    if (this.chapterCount > 1) {
      return true;
    }

    return false;
  }
}
